import {EBirdChecklistByFamily, EBirdChecklistBySpecies, EBirdMyDataSchema} from "ebird-mydata-reader";
import {formatDate, parseObservationDateTime} from "../../helpers/helpers";

export type ChecklistFilter = 'all'|'seen'|'unseen'|'extinct';

type FamilyExplorerSpeciesImage = EBirdChecklistBySpecies['image'] & {
    sourceUrl?: string | null;
    photographer?: string | null;
    attribution?: string | null;
}

export const getFamilyStats = (family: EBirdChecklistByFamily) => {
    const extantSpecies = family.speciesList.filter(species => !species.isExtinct);
    const seenSpecies = extantSpecies.filter(species => species.seen).length;
    const totalSpecies = extantSpecies.length;
    const extinctSpecies = family.speciesList.length - totalSpecies;

    return {
        seenSpecies,
        totalSpecies,
        unseenSpecies: Math.max(totalSpecies - seenSpecies, 0),
        extinctSpecies,
        isExtinct: totalSpecies === 0 && extinctSpecies > 0,
        seenPercent: Math.round(totalSpecies ? (seenSpecies / totalSpecies) * 100 : 0)
    };
}

export const getImageAttributionUrl = (image: EBirdChecklistBySpecies['image']) => {
    return (image as FamilyExplorerSpeciesImage | undefined)?.sourceUrl ?? undefined;
}

export const getImageAttributionLabel = (image: EBirdChecklistBySpecies['image']) => {
    const resolvedImage = image as FamilyExplorerSpeciesImage | undefined;
    const photographer = resolvedImage?.photographer?.trim();
    if (photographer) return photographer;

    const attribution = resolvedImage?.attribution?.trim();
    return attribution || undefined;
}

export const getFamilyScientificName = (family: EBirdChecklistByFamily) => {
    return family.familyScientific || family.familyName;
}

export const getFamilyCommonName = (family: EBirdChecklistByFamily) => {
    return family.familyCommon || family.familyName;
}

export const getSpeciesObservationTime = (observation: EBirdMyDataSchema) => {
    return parseObservationDateTime(observation.date, observation.time)?.toMillis() ?? 0;
}

export const getShortDate = (date?: string) => {
    return date ? formatDate(date).replace(/^([A-Za-z]+), /, '') : '';
}

export const getObservationLocation = (observation?: EBirdMyDataSchema) => {
    if (!observation) return '';

    return [observation.location, observation.county, observation.stateProvince].filter(Boolean).join(', ');
}

export const titleCaseRange = (range?: string) => {
    if (!range) return '';

    const smallWords = new Set(['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'near', 'of', 'on', 'or', 'the', 'to', 'via', 'with']);

    return range
        .toLowerCase()
        .split(/(\s+|[-/])/)
        .map((part, index) => {
            if (!part.trim() || part === '-' || part === '/') return part;
            if (index > 0 && smallWords.has(part)) return part;
            return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join('');
}

export const getSpeciesMapUrl = (observations: EBirdMyDataSchema[]) => {
    const coordinatesByLocation = new Map<string, EBirdMyDataSchema>();

    for (const observation of observations) {
        if (!Number.isFinite(observation.latitude) || !Number.isFinite(observation.longitude)) continue;
        coordinatesByLocation.set(observation.locationId || observation.location, observation);
    }

    const pins = Array.from(coordinatesByLocation.values())
        .slice(0, 12)
        .map(observation => `pin-s+1f5b3c(${observation.longitude},${observation.latitude})`);

    if (!pins.length) return undefined;

    return `https://api.mapbox.com/styles/v1/mapbox/outdoors-v12/static/${pins.join(',')}/auto/760x420@2x?padding=54&access_token=pk.eyJ1Ijoib2hpb2RhdmUiLCJhIjoiY2swMDZqdWw1MjVxeDNqcW9uaG8xcW9nbyJ9.Hlqy7TBoh8QIPWC5j7AQNQ`;
}

export const getSpeciesStatusLabel = (species: EBirdChecklistBySpecies) => {
    if (species.isExtinct) return 'Extinct';
    return species.seen ? 'Seen' : 'Not Seen';
}
